import { GatsbyLinkProps, Link as GatsbyLink } from 'gatsby'
import React, { forwardRef } from 'react'

import { usePageUrl } from '../../lib/pages/hooks/usePageUrl'
import { normalizeLink } from '../../lib/pages/links'
import { PageUrlOptions } from '../../lib/pages/types'

// https://www.polynique.com/web-development/extend-gatsby-link-in-typescript/
// https://github.com/gatsbyjs/gatsby/issues/16682
// https://github.com/gatsbyjs/gatsby/issues/27035
export type LinkProps = Omit<GatsbyLinkProps<Record<string, unknown>>, 'ref' | 'to'> &
  PageUrlOptions & {
    to: GatsbyLinkProps<Record<string, unknown>>['to'] | undefined
  }

export type LinkRef = HTMLAnchorElement & GatsbyLink<Record<string, unknown>>

const Link = forwardRef<LinkRef, LinkProps>(function Link(
  { children, to, activeClassName, partiallyActive, forceBase, ...otherProps },
  ref,
) {
  // Use Gatsby Link for internal links, and <a> for others
  const { getPagePath } = usePageUrl()
  const { target, rel, ...remainingAnchorProps } = otherProps

  const link = normalizeLink(to, { target })

  if (link.type === 'gatsby') {
    return (
      <GatsbyLink
        to={getPagePath(link.to, { forceBase })}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        ref={ref}
        {...otherProps}
      >
        {children}
      </GatsbyLink>
    )
  }

  // determine target prop (if any)
  const targetProps = {
    ...(link.blank ? { target: '_blank' } : {}),
    ...(target ? { target } : {}),
  }
  // set noopener if opening new tab unless overridden in props
  const relProps = {
    ...(targetProps.target === '_blank' ? { rel: 'noopener' } : {}),
    ...(rel ? { rel } : {}),
  }

  // collect anchor props
  const anchorProps = {
    ...(link.href ? { href: link.href } : {}),
    ...targetProps,
    ...relProps,
    ...remainingAnchorProps,
  }

  return (
    <a ref={ref} {...anchorProps}>
      {children}
    </a>
  )
})

export default Link
